import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { mapCrmError } from './crm-api.errors';
import { CRM_API_URL } from './crm-api.tokens';
import {
  FaqCategoryDto,
  FaqCategoryListQuery,
  FaqCategoryListResult,
  FaqCategoryRequest,
  FaqCategoryStatusOption,
} from './faq-categories-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const FAQ_CATEGORIES_PATH = '/customer-service/faq-categories';

@Injectable({ providedIn: 'root' })
export class FaqCategoriesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(CRM_API_URL).replace(/\/+$/, '')}${FAQ_CATEGORIES_PATH}`;

  list(query: FaqCategoryListQuery = {}): Observable<FaqCategoryListResult> {
    return this.http
      .get<unknown>(this.baseUrl, { params: this.listParams(query) })
      .pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(body => unwrapList(body)),
        catchError(error => throwError(() => mapCrmError(error))),
      );
  }

  filterSelects(): Promise<FaqCategoryStatusOption[]> {
    return firstValueFrom(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(body => unwrapFilterSelects(body)),
        catchError(error => throwError(() => mapCrmError(error))),
      ),
    );
  }

  getById(uuid: string): Promise<FaqCategoryDto> {
    return this.mutate(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      unwrapItem,
    );
  }

  create(payload: FaqCategoryRequest): Promise<FaqCategoryDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapItem);
  }

  update(uuid: string, payload: FaqCategoryRequest): Promise<FaqCategoryDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapItem,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  private async mutate<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapCrmError(error);
    }
  }

  private listParams(query: FaqCategoryListQuery): HttpParams {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));

    if (query.name) params = params.set('filters[name]', query.name);
    if (query.description) params = params.set('filters[description]', query.description);
    if (query.status?.length) {
      params = params.set('select[status]', query.status.join(','));
    }
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.order) params = params.set('order', query.order);

    return params;
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds =
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): FaqCategoryListResult {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalizeItem), total: data.length };
  }

  if (data && typeof data === 'object') {
    const page = data as {
      data?: unknown;
      pagination?: { totalRecords?: unknown };
    };
    if (Array.isArray(page.data)) {
      const totalRecords = page.pagination?.totalRecords;
      return {
        items: page.data.map(normalizeItem),
        total: typeof totalRecords === 'number' ? totalRecords : page.data.length,
      };
    }
  }

  return { items: [], total: 0 };
}

function unwrapItem(body: unknown): FaqCategoryDto {
  return normalizeItem(unwrapData(body));
}

function unwrapFilterSelects(body: unknown): FaqCategoryStatusOption[] {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object' || !('status' in data)) {
    return [];
  }
  const status = (data as { status?: unknown }).status;
  if (!Array.isArray(status)) return [];

  return status.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const option = item as { uuid?: unknown; name?: unknown };
    if (typeof option.uuid !== 'string' || typeof option.name !== 'string') {
      return [];
    }
    return [{ uuid: option.uuid, name: option.name }];
  });
}

function normalizeItem(value: unknown): FaqCategoryDto {
  const item =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const status =
    item['status'] && typeof item['status'] === 'object'
      ? (item['status'] as Record<string, unknown>)
      : {};

  return {
    uuid: typeof item['uuid'] === 'string' ? item['uuid'] : '',
    name: typeof item['name'] === 'string' ? item['name'] : '',
    description: typeof item['description'] === 'string' ? item['description'] : null,
    status: {
      name: typeof status['name'] === 'string' ? status['name'] : '',
      value: typeof status['value'] === 'string' ? status['value'] : '',
      tone: typeof status['tone'] === 'string' ? status['tone'] : '',
    },
  };
}
